// src/components/ui/ContactForm.jsx - Updated with date preferences
import React, { useState, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import Button from './Button';

const ContactForm = ({ onSubmit, formStatus }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    date: '',
    time: '',
    preferredMethod: 'email'
  });
  
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  
  // Load the reCAPTCHA v3 script
  useEffect(() => {
    // Load the reCAPTCHA script if it's not already loaded
    if (!window.grecaptcha) {
      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js?render=6LfehhsrAAAAAHqmhgyDIQ-f3lMnYjmK-q4dA2i8';   //Site key from Google
      script.async = true;
      script.onload = () => setRecaptchaLoaded(true);
      document.body.appendChild(script);
      
      return () => {
        // Cleanup if component unmounts
        document.body.removeChild(script);
      };
    } else {
      setRecaptchaLoaded(true);
    }
  }, []);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    // Required fields validation
    if (!formData.name) newErrors.name = 'Name is required';
    if (!formData.email) newErrors.email = 'Email is required';
    
    // Email format validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Message validation
    if (!formData.message) newErrors.message = 'Message is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setSubmitting(true);
      
      // Execute reCAPTCHA
      if (window.grecaptcha && recaptchaLoaded) {
        window.grecaptcha.ready(() => {
          window.grecaptcha
            .execute('6LfehhsrAAAAAHqmhgyDIQ-f3lMnYjmK-q4dA2i8', { action: 'submit_contact_form' })  //site key from google
            .then(token => {
              // Add the token to form data
              const formDataWithToken = {
                ...formData,
                'g-recaptcha-response': token
              };
              
              // Submit the form
              onSubmit(formDataWithToken);
              
              // Reset form
              setFormData({
                name: '',
                email: '',
                phone: '',
                subject: '',
                message: '',
                date: '',
                time: '',
                preferredMethod: 'email'
              });
            })
            .catch(error => {
              console.error('reCAPTCHA error:', error);
              setSubmitting(false);
              
              // Show error in form status
              onSubmit({ 
                error: 'Failed to verify reCAPTCHA. Please try again or contact us directly.' 
              });
            });
        });
      } else {
        // If reCAPTCHA isn't loaded, submit without it
        console.warn('reCAPTCHA not loaded, submitting without verification');
        onSubmit(formData);
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          date: '',
          time: '',
          preferredMethod: 'email'
        });
      }
    }
  };

  return (
    <div>
      <h3 className="text-2xl font-serif font-bold text-primary mb-6">Send Us a Message</h3>
      
      {formStatus.visible && (
        <div 
          className={`p-4 mb-6 rounded-lg ${
            formStatus.type === 'success' 
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {formStatus.message}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4" action="mailto:info@amvlaw.io" method="post" encType="text/plain">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="name" className="block text-gray-700 font-medium mb-2">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input 
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                errors.name ? 'border-red-500 bg-red-50' : 'border-gray-300'
              }`}
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name}</p>
            )}
          </div>
          
          <div>
            <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input 
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                errors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'
              }`}
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="phone" className="block text-gray-700 font-medium mb-2">
              Phone Number
            </label>
            <input 
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          
          <div>
            <label htmlFor="subject" className="block text-gray-700 font-medium mb-2">
              Subject
            </label>
            <input 
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
        </div>
        
        <div>
          <label htmlFor="preferredMethod" className="block text-gray-700 font-medium mb-2">
            Preferred Contact Method
          </label>
          <select
            id="preferredMethod"
            name="preferredMethod"
            value={formData.preferredMethod}
            onChange={handleChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="email">Email</option>
            <option value="phone">Phone</option>
          </select>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="date" className="block text-gray-700 font-medium mb-2">
              Preferred Date for Call Back (Optional)
            </label>
            <input 
              type="date"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          
          <div>
            <label htmlFor="time" className="block text-gray-700 font-medium mb-2">
              Preferred Time (Optional)
            </label>
            <select
              id="time"
              name="time"
              value={formData.time}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">Select a time</option>
              <option value="9:30 AM - 11:30 AM">9:30 AM - 11:30 AM</option>
              <option value="11:30 AM - 1:30 PM">11:30 AM - 1:30 PM</option>
              <option value="2:30 PM - 4:30 PM">2:30 PM - 4:30 PM</option>
              <option value="4:30 PM - 6:30 PM">4:30 PM - 6:30 PM</option>
            </select>
          </div>
        </div>
        
        <div>
          <label htmlFor="message" className="block text-gray-700 font-medium mb-2">
            Your Message <span className="text-red-500">*</span>
          </label>
          <textarea 
            id="message"
            name="message"
            rows="5"
            value={formData.message}
            onChange={handleChange}
            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
              errors.message ? 'border-red-500 bg-red-50' : 'border-gray-300'
            }`}
          ></textarea>
          {errors.message && (
            <p className="text-red-500 text-sm mt-1">{errors.message}</p>
          )}
        </div>
        
        {/* reCAPTCHA v3 doesn't show a visible widget, it works in the background */}
        <div className="text-sm text-gray-500 mt-2 mb-6">
          This site is protected by reCAPTCHA v3 and the Google 
          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-dark"> Privacy Policy</a> and
          <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-dark"> Terms of Service</a> apply.
        </div>
        
        <Button
          type="submit"
          variant="primary"
          fullWidth
          size="lg"
          className="inline-flex items-center"
          disabled={submitting}
        >
          {submitting ? 'Sending...' : 'Send Message'}
          {!submitting && <ArrowRight className="ml-2 h-5 w-5" />}
        </Button>
      </form>
    </div>
  );
};

export default ContactForm;